import { useCallback, useMemo, useState } from 'react'
import type { SelectProps } from '@mantine/core'
import { Box, Flex } from '@mantine/core'
import { useIsFetching, useIsMutating } from '@tanstack/react-query'

import type { Chart, ChartsData, List, Users } from '@/types/pdfExport.types'

import { AgeSummaryShell } from '@/modules/admin/pages/components/AgeSummaryShell/AgeSummaryShell'
import { GenderSummaryShell } from '@/modules/admin/pages/components/GenderSummaryShell/GenderSummaryShell'
import { IncomeSummaryShell } from '@/modules/admin/pages/components/IncomeSummaryShell/IncomeSummaryShell'
import { RaceSummaryShell } from '@/modules/admin/pages/components/RaceSummaryShell/RaceSummaryShell'
import { TopicsSummaryShell } from '@/modules/admin/pages/components/TopicsSummaryShell/TopicsSummaryShell'
import { UsersSummaryShell } from '@/modules/admin/pages/components/UsersSummaryShell/UsersSummaryShell'

import { Button } from '@/components/Button/Button'
import { SelectInput } from '@/components/SelectInput/SelectInput'
import { StatsExport } from '@/components/StatsExport/StatsExport'
import { generatePdf } from '@/components/StatsExport/StatsExport.config'

import { useAdminCompaniesServiceAdminCompaniesControllerGetCompanies } from '@/__generated__/queries'

export function Component() {
  const [selectedCompany, setSelectedCompany] = useState<string | undefined>(
    undefined,
  )
  const [isGenerating, setIsGenerating] = useState(false)
  const [chartsData, setChartsData] = useState<ChartsData>({
    age: undefined,
    gender: undefined,
    income: undefined,
    race: undefined,
    topics: undefined,
    users: undefined,
  })

  const { data } =
    useAdminCompaniesServiceAdminCompaniesControllerGetCompanies()

  const isMutating = useIsMutating()
  const isFetching = useIsFetching()

  const companies = useMemo<SelectProps['data']>(() => {
    if (!data) return []

    return data.map(company => ({
      label: company.name,
      value: company.id.toString(),
    }))
  }, [data])

  const handleExportToPDF = () => {
    setIsGenerating(true)

    const selectedCompanyData = data?.find(
      company => company.id.toString() === selectedCompany,
    )

    generatePdf(
      <StatsExport
        chartsData={chartsData}
        companyName={selectedCompanyData?.name}
      />,
      setIsGenerating,
    )
  }

  const handleUpdateChartsData = useCallback(
    (data: Users | List[] | Chart, key: string) => {
      setChartsData(prev => ({ ...prev, [key]: data }))
    },
    [],
  )

  const handleCompanyChange = (value: string | null) => {
    setSelectedCompany(value || undefined)
  }

  const handleResetCompany = () => {
    setSelectedCompany(undefined)
  }

  return (
    <Box maw="1600px" mx="auto">
      <Flex
        align="center"
        direction={{
          base: 'column',
          sm: 'row',
        }}
        gap={16}
        justify="space-between"
        mb={24}
      >
        <SelectInput
          clearable
          data={companies}
          mb={0}
          miw={{
            base: '100%',
            sm: 240,
          }}
          onChange={value => handleCompanyChange(value)}
          onClear={handleResetCompany}
          placeholder="Select company"
        />

        <Button
          disabled={!!isFetching || !!isMutating}
          h={38}
          loading={isGenerating}
          miw={100}
          onClick={handleExportToPDF}
          size="md"
          w={{
            base: '100%',
            sm: 'auto',
          }}
        >
          Export to PDF
        </Button>
      </Flex>

      <Box>
        <Flex
          direction={{
            base: 'column',
            lg: 'row',
          }}
          gap={24}
        >
          <UsersSummaryShell
            company={selectedCompany}
            onUpdateChartsData={handleUpdateChartsData}
          />

          <GenderSummaryShell
            company={selectedCompany}
            onUpdateChartsData={handleUpdateChartsData}
          />
        </Flex>

        <Flex
          direction={{
            base: 'column',
            lg: 'row',
          }}
          gap={24}
          mt={24}
        >
          <AgeSummaryShell
            company={selectedCompany}
            onUpdateChartsData={handleUpdateChartsData}
          />

          <IncomeSummaryShell
            company={selectedCompany}
            onUpdateChartsData={handleUpdateChartsData}
          />
        </Flex>

        <Flex
          direction={{
            base: 'column',
            lg: 'row',
          }}
          gap={24}
          mt={24}
        >
          <RaceSummaryShell
            company={selectedCompany}
            onUpdateChartsData={handleUpdateChartsData}
          />

          <TopicsSummaryShell
            company={selectedCompany}
            onUpdateChartsData={handleUpdateChartsData}
          />
        </Flex>
      </Box>
    </Box>
  )
}
